
import { useEffect, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { DataTable, Column } from "@/components/ui/data-table"
import { userService } from "@/services/user.service"
import { User } from "@/types/api"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Edit2, Trash2, Loader2 } from "lucide-react"
import { UserEditDialog } from "@/components/users/UserEditDialog"
import { useToast } from "@/hooks/use-toast"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

export default function UsersPage() {
  const [users, setUsers] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [editUser, setEditUser] = useState<User | null>(null)
  const [deleteUserId, setDeleteUserId] = useState<number | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const { toast } = useToast()
  const PAGE_SIZE = 10

  const fetchUsers = async () => {
    setIsLoading(true)
    try {
      const response = await userService.getUsers({
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      })
      setUsers(response.results)
      setTotalCount(response.count)
    } catch (error) {
      console.error(error)
      toast({
        title: "Ошибка",
        description: "Не удалось загрузить пользователей",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchUsers()
  }, [page])

  const handleDelete = async () => {
    if (!deleteUserId) return
    setIsDeleting(true)
    try {
      await userService.deleteUser(deleteUserId)
      toast({
        title: "Успешно",
        description: "Пользователь успешно удален",
      })
      fetchUsers()
    } catch (error) {
      console.error(error)
      toast({
        title: "Ошибка",
        description: "Не удалось удалить пользователя",
        variant: "destructive",
      })
    } finally {
      setIsDeleting(false)
      setDeleteUserId(null)
    }
  }

  const columns: Column<User>[] = [
    {
      header: "Пользователь",
      cell: (user) => (
        <div className="flex items-center gap-3">
          <Avatar>
            <AvatarImage src={user.avatar || undefined} />
            <AvatarFallback>{user.full_name?.charAt(0) || "U"}</AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium">{user.full_name}</div>
            <div className="text-sm text-gray-500">{user.username}</div>
          </div>
        </div>
      ),
    },
    {
      header: "Контакты",
      cell: (user) => (
        <div>
          <div className="text-sm">{user.email || "-"}</div>
          <div className="text-sm text-gray-500">{user.phone || "-"}</div>
        </div>
      ),
    },
    {
      header: "Роль",
      cell: (user) => (
        <span className={`text-xs px-2 py-1 rounded-full ${user.is_superuser ? 'bg-purple-100 text-purple-700' : user.is_staff ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`}>
          {user.is_superuser ? "Супер Админ" : user.is_staff ? "Персонал" : "Пользователь"}
        </span>
      )
    },
    {
      header: "Статус",
      cell: (user) => (
        <span className={`text-xs px-2 py-1 rounded-full ${user.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {user.is_active !== false ? "Активный" : "Неактивный"}
        </span>
      )
    },
    {
      header: "Создан",
      accessorKey: "created_at",
      cell: (user) => new Date(user.created_at).toLocaleDateString(),
    },
    {
      header: "Действия",
      className: "text-right",
      cell: (user) => (
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setEditUser(user)}
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-red-500 hover:text-red-700"
            onClick={() => setDeleteUserId(user.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Пользователи</h1>
        <p className="text-gray-600 mt-1">Управление пользователями системы и их правами.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Список пользователей</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={users}
            isLoading={isLoading}
            pageCount={Math.ceil(totalCount / PAGE_SIZE)}
            currentPage={page}
            onPageChange={setPage}
          />
        </CardContent>
      </Card>

      <UserEditDialog
        user={editUser}
        open={!!editUser}
        onOpenChange={(open) => !open && setEditUser(null)}
        onSuccess={fetchUsers}
      />

      <AlertDialog open={!!deleteUserId} onOpenChange={(open) => !open && setDeleteUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Вы уверены?</AlertDialogTitle>
            <AlertDialogDescription>
              Это действие нельзя отменить. Это навсегда удалит учетную запись пользователя.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={isDeleting} className="bg-red-600 hover:bg-red-700">
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Удаление...
                </>
              ) : (
                "Удалить"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
